// Iter 26 — Referral Leaderboard
//
// Public page that shows top referrers (monthly + all time) to encourage viral
// growth. Names are anonymized server-side (first + last initial). Logged-in
// users also see their personal rank highlighted.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Trophy, Gift, ArrowLeft, Crown, Users, TrendingUp, Sparkles } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function ReferralLeaderboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/referral/leaderboard?period=${period}&limit=20`)
      .then((r) => { setItems(r.data?.items || []); setDisabled(false); })
      .catch((e) => {
        if (e?.response?.status === 404) setDisabled(true);
      })
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    if (!user) return;
    api.get("/referral/me").then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  const top3 = items.slice(0, 3);
  const rest = items.slice(3);

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="referral-leaderboard-page">
      <Link to="/dashboard" className="text-sm flex items-center gap-2" style={{color: "var(--app-muted)"}} data-testid="leaderboard-back">
        <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.back") || "Beranda"}
      </Link>

      <div>
        <div className="label-caps flex items-center gap-2 mb-2" style={{color: "var(--app-brand)"}}>
          <Trophy size={12} strokeWidth={2} /> {t("leaderboard.label")}
        </div>
        <h1 className="font-display text-3xl sm:text-5xl tracking-tight">{t("leaderboard.title")}</h1>
        <p className="text-zinc-600 mt-2 text-sm sm:text-base max-w-2xl">
          {t("leaderboard.desc")}
        </p>
      </div>

      {/* My rank card (auth users only) */}
      {user && me && (
        <div
          className="p-4 sm:p-5 flex items-center justify-between gap-3 border-2"
          style={{borderColor: "var(--app-brand)", backgroundColor: "var(--app-brand-soft)"}}
          data-testid="my-rank-card"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full flex items-center justify-center font-display text-base text-white flex-shrink-0" style={{backgroundColor: "var(--app-brand)"}}>
              {me.my_rank ? `#${me.my_rank}` : "—"}
            </div>
            <div className="min-w-0">
              <div className="label-caps text-[10px]" style={{color: "var(--app-muted)"}}>{t("leaderboard.your_rank")}</div>
              <div className="font-medium text-sm sm:text-base truncate">
                {me.referral_code ? <>@{me.referral_code}</> : t("leaderboard.no_code_yet")}
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-display text-lg sm:text-2xl tracking-tight" style={{color: "var(--app-brand)"}}>
              {formatIDR(me.earnings_total || 0)}
            </div>
            <div className="text-[10px] uppercase tracking-wider" style={{color: "var(--app-muted)"}}>
              {me.referred_count || 0} {t("leaderboard.friends")}
            </div>
          </div>
        </div>
      )}

      {/* Period toggle */}
      {!disabled && (
        <div className="inline-grid grid-cols-2 gap-0 border" style={{borderColor: "var(--app-border)"}} data-testid="leaderboard-period-toggle">
        <button
          type="button"
          onClick={() => setPeriod("month")}
          className="px-4 py-2 text-sm transition-colors"
          style={{
            borderRight: "1px solid var(--app-border)",
            backgroundColor: period === "month" ? "var(--app-brand)" : "transparent",
            color: period === "month" ? "#fff" : "var(--app-fg)",
          }}
          data-testid="period-month"
        >
          {t("leaderboard.this_month")}
        </button>
        <button
          type="button"
          onClick={() => setPeriod("all")}
          className="px-4 py-2 text-sm transition-colors"
          style={{
            backgroundColor: period === "all" ? "var(--app-brand)" : "transparent",
            color: period === "all" ? "#fff" : "var(--app-fg)",
          }}
          data-testid="period-all"
        >
          {t("leaderboard.all_time")}
        </button>
      </div>
      )}

      {/* Content */}
      {disabled ? (
        <div className="border border-dashed py-16 text-center" style={{borderColor: "var(--app-border)"}} data-testid="leaderboard-disabled">
          <Trophy size={32} strokeWidth={1.5} className="mx-auto mb-3" style={{color: "var(--app-muted)"}} />
          <p className="text-sm" style={{color: "var(--app-muted)"}}>{t("leaderboard.disabled_msg")}</p>
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-sm" style={{color: "var(--app-muted)"}}>{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed py-16 text-center" style={{borderColor: "var(--app-border)"}} data-testid="leaderboard-empty">
          <Sparkles size={32} strokeWidth={1.5} className="mx-auto mb-3" style={{color: "var(--app-muted)"}} />
          <p className="text-sm" style={{color: "var(--app-muted)"}}>{t("leaderboard.empty")}</p>
          {user && (!me || !me.referral_code) && (
            <Link to="/profile" className="btn-primary-flat inline-flex text-sm mt-4" data-testid="cta-create-code">
              <Gift size={14} strokeWidth={1.5} /> {t("leaderboard.cta_create_code")}
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Top 3 podium */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {top3.map((it) => (
              <PodiumCard key={it.user_id || it.rank} item={it} t={t} isMe={user && me?.referral_code === it.referral_code} />
            ))}
          </div>

          {/* Rest list */}
          {rest.length > 0 && (
            <div className="border divide-y" style={{borderColor: "var(--app-border)"}} data-testid="leaderboard-rest">
              {rest.map((it) => (
                <LeaderRow key={it.user_id || it.rank} item={it} t={t} isMe={user && me?.referral_code === it.referral_code} />
              ))}
            </div>
          )}
        </>
      )}

      {/* CTA */}
      {user && me?.referral_code && (
        <div className="border-l-4 p-4 sm:p-5" style={{borderColor: "var(--app-brand)", backgroundColor: "var(--app-surface-alt)"}} data-testid="leaderboard-share-cta">
          <div className="label-caps mb-1" style={{color: "var(--app-brand)"}}>{t("leaderboard.climb_label")}</div>
          <p className="text-sm">{t("leaderboard.climb_desc")}</p>
          <Link to="/profile" className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium hover:underline" style={{color: "var(--app-brand)"}}>
            {t("leaderboard.go_to_profile")} →
          </Link>
        </div>
      )}
    </div>
  );
}

function PodiumCard({ item, t, isMe }) {
  const medal = item.rank === 1 ? "🥇" : item.rank === 2 ? "🥈" : "🥉";
  const big = item.rank === 1;
  return (
    <div
      className="border p-4 sm:p-5 text-center transition-all hover:translate-y-[-2px]"
      style={{
        borderColor: isMe ? "var(--app-brand)" : (big ? "var(--app-brand)" : "var(--app-border)"),
        backgroundColor: isMe ? "var(--app-brand-soft)" : (big ? "var(--app-brand-soft)" : "transparent"),
        order: item.rank === 1 ? 0 : (item.rank === 2 ? -1 : 1),
      }}
      data-testid={`podium-${item.rank}`}
    >
      <div className="text-3xl sm:text-4xl mb-1" aria-hidden>{medal}</div>
      <div className="label-caps text-[10px]" style={{color: "var(--app-muted)"}}>#{item.rank}</div>
      <div className="font-display text-base sm:text-lg tracking-tight mt-1 flex items-center justify-center gap-1.5 truncate">
        {item.display_name}
        {item.verified_badge && <VerifiedBadge size={12} />}
      </div>
      {item.referral_code && (
        <div className="font-mono text-[10px] mt-0.5 truncate" style={{color: "var(--app-muted)"}}>
          @{item.referral_code}
        </div>
      )}
      <div className="font-display text-xl sm:text-2xl tracking-tight mt-3" style={{color: "var(--app-brand)"}} data-testid={`podium-${item.rank}-earned`}>
        {formatIDR(item.total_earned)}
      </div>
      <div className="flex items-center justify-center gap-3 mt-2 text-[10px] sm:text-xs" style={{color: "var(--app-muted)"}}>
        <span className="inline-flex items-center gap-1"><Users size={10} strokeWidth={1.5} /> {item.unique_referees}</span>
        <span className="inline-flex items-center gap-1"><TrendingUp size={10} strokeWidth={1.5} /> {item.earnings_count} {t("leaderboard.tx_short")}</span>
      </div>
    </div>
  );
}

function LeaderRow({ item, t, isMe }) {
  return (
    <div
      className="flex items-center gap-3 p-3 sm:p-4 transition-colors"
      style={{backgroundColor: isMe ? "var(--app-brand-soft)" : "transparent"}}
      data-testid={`leaderboard-row-${item.rank}`}
    >
      <div className="w-8 sm:w-10 text-center font-mono text-sm flex-shrink-0" style={{color: "var(--app-muted)"}}>
        #{item.rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate flex items-center gap-1.5">
          {item.display_name}
          {item.verified_badge && <VerifiedBadge size={11} />}
          {isMe && <span className="label-caps text-[8px] px-1 py-0.5 text-white" style={{backgroundColor: "var(--app-brand)"}}>YOU</span>}
        </div>
        <div className="font-mono text-[10px]" style={{color: "var(--app-muted)"}}>
          {item.referral_code ? `@${item.referral_code}` : "—"} · {item.unique_referees} {t("leaderboard.friends_short")}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-mono font-semibold text-sm" style={{color: "var(--app-brand)"}}>{formatIDR(item.total_earned)}</div>
        <div className="text-[10px]" style={{color: "var(--app-muted)"}}>{item.earnings_count} {t("leaderboard.tx_short")}</div>
      </div>
    </div>
  );
}
